import { useState } from 'react'
import { usePage } from '@inertiajs/react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { instrumentsPath } from '@/shared/lib/routes'
import type {
  WebFrequencyType,
  WebInstrumentTaskFrequencyOverviewRow,
  WebInstrumentTaskIntervalType,
} from '@/shared/types/web'
import type { SharedProps } from '@/shared/types/SharedProps'
import { formatInstantInSiteZone } from '@/shared/lib/tenantDateTime'

const MILLISECONDS_PER_DAY = 86_400_000
const TIMELINE_PREVIEW_HORIZON_DAYS = 90

interface FrequenciesPageProps {
  frequencyTypes: WebFrequencyType[]
  intervalTypes: WebInstrumentTaskIntervalType[]
  instrumentTasks: WebInstrumentTaskFrequencyOverviewRow[]
  instrumentTasksShownCount?: number
  instrumentTasksTotalCount?: number
  instrumentTasksListTruncated?: boolean
  [key: string]: unknown
}

const resolveIntervalTypeDescription = (
  intervalTypes: WebInstrumentTaskIntervalType[],
  intervalTypeId: number | null | undefined,
): string => {
  if (!intervalTypeId) {
    return ''
  }
  const intervalTypeRow = intervalTypes.find((row) => row.id === intervalTypeId)
  return intervalTypeRow?.intervalTypeDescription ?? ''
}

const resolveFrequencyTypeCodeForInterval = (
  intervalTypes: WebInstrumentTaskIntervalType[],
  intervalTypeId: number | null | undefined,
): string => {
  if (!intervalTypeId) {
    return ''
  }
  const intervalTypeRow = intervalTypes.find((row) => row.id === intervalTypeId)
  return intervalTypeRow?.frequencyTypeCode ?? ''
}

const timelineLaneDescription = (taskRow: WebInstrumentTaskFrequencyOverviewRow): string => {
  if (taskRow.timelineLaneState === 'computed') {
    return t('instruments.taskFrequencyOverview.timelineLane.computed')
  }
  switch (taskRow.timelineLaneReason) {
    case 'missing_interval_type':
      return t('instruments.taskFrequencyOverview.timelineLane.notComputed.missing_interval_type')
    case 'calendar_fixed':
      return t('instruments.taskFrequencyOverview.timelineLane.notComputed.calendar_fixed')
    case 'unscheduled':
      return t('instruments.taskFrequencyOverview.timelineLane.notComputed.unscheduled')
    case 'not_timed':
      return t('instruments.taskFrequencyOverview.timelineLane.notComputed.not_timed')
    case 'missing_interval_hours':
      return t('instruments.taskFrequencyOverview.timelineLane.notComputed.missing_interval_hours')
    case 'no_completion_log':
      return t('instruments.taskFrequencyOverview.timelineLane.notComputed.no_completion_log')
    case 'no_upcoming_within_window':
      return t(
        'instruments.taskFrequencyOverview.timelineLane.notComputed.no_upcoming_within_window',
      )
    case 'missing_payload':
      return t('instruments.taskFrequencyOverview.timelineLane.notComputed.missing_payload')
    default:
      return t('instruments.taskFrequencyOverview.timelineLane.notComputed.generic')
  }
}

const TaskTimelinePreview = ({
  taskRow,
  tenantTimeFormat,
}: {
  taskRow: WebInstrumentTaskFrequencyOverviewRow
  tenantTimeFormat: number | null | undefined
}) => {
  const siteTimeZone = taskRow.timeZoneName
  const [horizonStartMilliseconds] = useState(() => Date.now())

  if (taskRow.timelineLaneState !== 'computed' || !taskRow.timelineDuePoints?.length) {
    return <span className="text-muted-foreground text-xs">{timelineLaneDescription(taskRow)}</span>
  }

  const horizonEndMilliseconds =
    horizonStartMilliseconds + TIMELINE_PREVIEW_HORIZON_DAYS * MILLISECONDS_PER_DAY
  const horizonSpan = horizonEndMilliseconds - horizonStartMilliseconds

  return (
    <div className="space-y-1">
      <div
        className="relative h-6 w-full max-w-[220px] rounded border bg-muted/40"
        title={t('instruments.taskFrequencyOverview.timelineStripTitle')}
      >
        <div className="absolute inset-y-0 left-0 w-px bg-border" />
        <div className="absolute inset-y-0 right-0 w-px bg-border" />
        {taskRow.timelineDuePoints.map((duePoint, index) => {
          const dueMilliseconds = new Date(duePoint.dueAt).getTime()
          const rawPercent = ((dueMilliseconds - horizonStartMilliseconds) / horizonSpan) * 100
          const clampedLeftPercent = Math.min(100, Math.max(0, rawPercent))
          return (
            <div
              key={`${duePoint.dueAt}-${index}`}
              className="absolute top-0 bottom-0 w-0.5 bg-primary"
              style={{ left: `${clampedLeftPercent}%` }}
              title={formatInstantInSiteZone(duePoint.dueAt, siteTimeZone, tenantTimeFormat)}
            />
          )
        })}
      </div>
      <ul className="text-xs text-muted-foreground space-y-0.5">
        {taskRow.timelineDuePoints.map((duePoint, index) => (
          <li key={`${duePoint.dueAt}-label-${index}`}>
            {formatInstantInSiteZone(duePoint.dueAt, siteTimeZone, tenantTimeFormat)}
          </li>
        ))}
      </ul>
    </div>
  )
}

const FrequenciesPage = () => {
  const page = usePage<SharedProps & FrequenciesPageProps>()
  const { tenantTimeFormat } = page.props
  const {
    intervalTypes,
    instrumentTasks,
    instrumentTasksShownCount,
    instrumentTasksTotalCount,
    instrumentTasksListTruncated,
  } = page.props

  useBreadcrumbs([
    { label: t('nav.instruments'), href: instrumentsPath() },
    { label: t('instruments.taskFrequencyOverview.title') },
  ])
  return (
    <div className="space-y-10 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">{t('instruments.taskFrequencyOverview.title')}</h1>
          <p className="text-muted-foreground max-w-3xl">
            {t('instruments.taskFrequencyOverview.intro')}
          </p>
        </div>
      </div>

      <section className="space-y-4 rounded-md border p-4">
        <h2 className="text-lg font-semibold">
          {t('instruments.taskFrequencyOverview.tasksHeading')}
        </h2>
        {instrumentTasksListTruncated &&
        typeof instrumentTasksShownCount === 'number' &&
        typeof instrumentTasksTotalCount === 'number' ? (
          <p
            className="text-sm text-muted-foreground max-w-3xl rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900"
            role="status"
          >
            {t('instruments.tasksSchedule.instrumentTaskPickerTruncated')
              .replace('{shown}', String(instrumentTasksShownCount))
              .replace('{total}', String(instrumentTasksTotalCount))}
          </p>
        ) : null}
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('instruments.taskFrequencyOverview.colInstrument')}</TableHead>
                <TableHead>{t('instruments.taskFrequencyOverview.colTask')}</TableHead>
                <TableHead>{t('instruments.taskFrequencyOverview.colFrequencyType')}</TableHead>
                <TableHead>
                  {t('instruments.taskFrequencyOverview.colFrequencyDefinition')}
                </TableHead>
                <TableHead>{t('instruments.taskFrequencyOverview.colTimeline')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {instrumentTasks.map((taskRow) => (
                <TableRow key={taskRow.id}>
                  <TableCell>
                    {taskRow.instrumentLabel ?? t('instruments.taskFrequencyOverview.noInstrument')}
                  </TableCell>
                  <TableCell className="font-medium">{taskRow.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {resolveFrequencyTypeCodeForInterval(
                      intervalTypes,
                      taskRow.instrumentTaskIntervalTypeId,
                    ) || '—'}
                  </TableCell>
                  <TableCell>
                    {resolveIntervalTypeDescription(
                      intervalTypes,
                      taskRow.instrumentTaskIntervalTypeId,
                    ) || '—'}
                  </TableCell>
                  <TableCell>
                    <TaskTimelinePreview taskRow={taskRow} tenantTimeFormat={tenantTimeFormat} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  )
}

export default FrequenciesPage
